import React, {useState} from 'react';
import {useTranslation} from '../../hooks/useTranslation';
import CorrectiveActionModal from '../../components/CorrectiveActionModal';

const CorrectiveActionsModal = ({
  correctiveActions,
  currentStep,
  setCurrentStep,
  onClose,
  inspection,
  setInspection,
}) => {
  const {t} = useTranslation();
  const [modalStatus, setModalStatus] = useState(false);

  if (!correctiveActions || correctiveActions.length === 0) {
    return null;
  }

  const totalSteps = correctiveActions.length;
  const currentAction = correctiveActions[currentStep - 1];
  const progressPercentage = (currentStep / totalSteps) * 100;

  const formatDate = dateString => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}.${day}.${year}`;
  };

  const handleKeepAndContinue = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    } else {
      onClose();
    }
    const newFields = inspection.fields.map(field => {
      if (field.id === currentAction.field_id) {
        return {
          ...field,
          comment_text: currentAction.comment,
          comment_files: currentAction.files,
          answers: ['C'],
          options: [
            ...field.options.map(option => {
              if (option.value === 'C') {
                return {
                  ...option,
                  from_corrective_action: true,
                };
              }
              return option;
            }),
          ],
        };
      }
      return field;
    });

    setInspection({...inspection, fields: newFields});
  };

  const onSubmit = () => {
    setModalStatus(true);
  };

  const onSuccess = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    } else {
      onClose();
    }
  };

  return (
    <div className='modal'>
      <div className='modal__content modal__content--large'>
        <div className='modal__content-head'>
          <p>{t('correctiveActionsModal.title')}</p>
        </div>
        <div className='modal__content-body corrective-actions-modal'>
          {totalSteps > 1 && (
            <div
              className={`${totalSteps > 1 ? 'corrective-actions-modal__step-indicator' : ''}`}
            >
              <p className='corrective-actions-modal__step-text'>
                {t('correctiveActionsModal.stepIndicator')}{' '}
                <span className='step-current'>{currentStep}</span>{' '}
                {t('correctiveActionsModal.from')}{' '}
                <span className='step-total'>{totalSteps}</span>
              </p>

              <div className='corrective-actions-modal__progress-bar'>
                <div
                  className='corrective-actions-modal__progress-fill'
                  style={{width: `${progressPercentage}%`}}
                ></div>
              </div>
            </div>
          )}

          <div className='corrective-actions-modal__date-row'>
            <span className='date-label'>
              {t('correctiveActionsModal.createdDate')}
            </span>
            <span className='date-value'>
              {formatDate(currentAction?.created_at || currentAction?.date)}
            </span>
          </div>

          <p className='corrective-actions-modal__message'>
            {t('correctiveActionsModal.message')}
          </p>

          <div className='corrective-actions-modal__section'>
            <label>{t('correctiveActionsModal.discrepancy')}</label>
            <p className='discrepancy-text'>
              {currentAction?.discrepancy ||
                currentAction?.title ||
                t('correctiveActionsModal.notAvailable')}
            </p>
          </div>

          <div className='corrective-actions-modal__section'>
            <label>{t('correctiveActionsModal.comment')}</label>
            <p className='comment-text'>
              "
              {currentAction?.comment ||
                currentAction?.description ||
                t('correctiveActionsModal.notAvailable')}
              "
            </p>
          </div>

          {currentAction?.comments_history &&
            currentAction.comments_history.length > 0 && (
              <div className='corrective-actions-modal__section'>
                <div className='remarks-history-container'>
                  <p className='remarks-history__intro'>
                    {t('correctiveActionModal.comments_history.description')}:
                  </p>
                  <div className='remarks-history'>
                    {currentAction.comments_history.map((remark, index) => (
                      <div key={index} className='remark-item'>
                        <div className='remark-item__date'>
                          {formatDate(remark.created_at)}
                        </div>
                        <div className='remark-item__row'>
                          <div className='remark-item__text'>
                            {remark.comment || ''}
                          </div>
                          {remark.created_by_user && (
                            <div className='remark-item__user'>
                              {t('correctiveActionsModal.submittedBy')}{' '}
                              {remark.created_by_user || ''}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

          {currentAction?.files && currentAction.files.length > 0 && (
            <div className='corrective-actions-modal__files'>
              <label>{t('correctiveActionsModal.files')}</label>
              <div className='files-grid'>
                {currentAction.files.map((file, index) => (
                  <div key={index} className='file-thumbnail'>
                    <img src={file.url || file} alt={`file-${index}`} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {currentAction?.photos && currentAction.photos.length > 0 && (
            <div className='corrective-actions-modal__files'>
              <label>{t('correctiveActionsModal.files')}</label>
              <div className='files-grid'>
                {currentAction.photos.map((photo, index) => (
                  <div key={index} className='file-thumbnail'>
                    <img src={photo.url || photo} alt={`photo-${index}`} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {currentAction?.attachments &&
            currentAction.attachments.length > 0 && (
              <div className='corrective-actions-modal__files'>
                <label>{t('correctiveActionsModal.files')}</label>
                <div className='files-grid'>
                  {currentAction.attachments.map((attachment, index) => (
                    <div key={index} className='file-thumbnail'>
                      <img
                        src={attachment.url || attachment}
                        alt={`attachment-${index}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
        </div>
        <div className='modal__content-bottom corrective-actions-modal__buttons'>
          <button
            className='corrective-actions-modal__buttons__keep'
            onClick={handleKeepAndContinue}
          >
            {t('correctiveActionsModal.keepAndContinue')}
          </button>
          <button
            className='corrective-actions-modal__buttons__go'
            onClick={onSubmit}
          >
            {t('correctiveActionsModal.goToCorrectiveActions')}
          </button>
        </div>
      </div>
      <div className='modal__bg'></div>

      {modalStatus && (
        <CorrectiveActionModal
          exit={() => setModalStatus(false)}
          action={currentAction}
          currentTab={0}
          onSuccess={onSuccess}
        />
      )}
    </div>
  );
};

export default CorrectiveActionsModal;
